"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useWeb3Modal, useWeb3ModalAccount } from "@/lib/web3modal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { GoldenEraBg } from "@/components/golden-era-bg";
import {
  ArrowRight,
  Ban,
  CloudLightning,
  Coins,
  Globe,
  Hexagon,
  Lock,
  RefreshCw,
  Search,
  Shield,
  Sparkles,
  Wallet,
  Waves,
} from "lucide-react";

interface LandingTLD {
  id: number;
  name: string;
  is_active: boolean;
  registration_open: boolean;
  domain_count: number;
  base_price_usd: string;
  pricing: {
    flrPriceUSD: number;
    tiers: {
      standard: { priceUSD: number };
    };
  };
}

interface PricingTier {
  criteria: string;
  priceUSD: number;
  priceFLR: number;
  renewalUSD: number;
}

interface PricingData {
  flrPriceUSD: number;
  priceSource: string;
  tiers: {
    ultra_premium: PricingTier;
    premium: PricingTier;
    standard_plus: PricingTier;
    standard: PricingTier;
  };
}

export default function Home() {
  const { open } = useWeb3Modal();
  const { isConnected } = useWeb3ModalAccount();
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [tlds, setTlds] = useState<LandingTLD[]>([]);
  const [tldsLoading, setTldsLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch("/api/proxy/pricing");
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (e) {
        console.error("Failed to fetch pricing:", e);
      }
    };

    fetchPricing();
    const interval = setInterval(fetchPricing, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTLDs = async () => {
      try {
        const res = await fetch("/api/proxy/tlds");
        if (res.ok) {
          const data = await res.json();
          const activeTlds = (data.tlds || []).filter(
            (t: LandingTLD) => t.is_active
          );
          setTlds(activeTlds);
        }
      } catch (e) {
        console.error("Failed to fetch TLDs:", e);
      } finally {
        setTldsLoading(false);
      }
    };
    fetchTLDs();
  }, []);

  // Scroll-reveal IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add("visible");
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const pricingTiers = pricing
    ? [
        {
          tier: "Ultra Premium",
          chars: "1 char",
          priceUSD: pricing.tiers.ultra_premium.priceUSD,
          priceFLR: pricing.tiers.ultra_premium.priceFLR,
          color: "platinum",
        },
        {
          tier: "Premium",
          chars: "2 chars",
          priceUSD: pricing.tiers.premium.priceUSD,
          priceFLR: pricing.tiers.premium.priceFLR,
          color: "gold",
        },
        {
          tier: "Standard Plus",
          chars: "3 chars",
          priceUSD: pricing.tiers.standard_plus.priceUSD,
          priceFLR: pricing.tiers.standard_plus.priceFLR,
          color: "rosegold",
          popular: true,
        },
        {
          tier: "Standard",
          chars: "4+ chars",
          priceUSD: pricing.tiers.standard.priceUSD,
          priceFLR: pricing.tiers.standard.priceFLR,
          color: "bronze",
        },
      ]
    : [
        { tier: "Ultra Premium", chars: "1 char", priceUSD: 500, priceFLR: 10000, color: "platinum" },
        { tier: "Premium", chars: "2 chars", priceUSD: 200, priceFLR: 4000, color: "gold" },
        { tier: "Standard Plus", chars: "3 chars", priceUSD: 50, priceFLR: 1000, color: "rosegold", popular: true },
        { tier: "Standard", chars: "4+ chars", priceUSD: 10, priceFLR: 200, color: "bronze" },
      ];

  const handleCTA = () => {
    if (isConnected) {
      window.location.href = "/register-v2";
    } else {
      open();
    }
  };

  return (
    <div className="flex flex-col relative">
      <GoldenEraBg />

      {/* 1. Hero Section */}
      <section className="relative py-16 md:py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 hero-halcyon-bg" />
        {/* Drifting ambient blurs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[hsl(var(--halcyon-teal)/0.08)] blur-3xl animate-drift" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-[hsl(var(--halcyon-green)/0.06)] blur-3xl animate-drift hidden sm:block" style={{ animationDelay: "-7s" }} />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 rounded-full bg-[hsl(var(--primary)/0.04)] blur-3xl animate-drift hidden sm:block" style={{ animationDelay: "-14s" }} />

        <div className="container relative z-10 max-w-4xl mx-auto text-center">
          <Badge variant="secondary" className="mb-6">
            Built on Handshake + Flare
          </Badge>

          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold tracking-tight mb-6">
            The{" "}
            <span className="text-halcyon-gradient">Golden Era</span>
            <br />
            of Digital Identity.
          </h1>

          <p className="text-base sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Own your name forever. Halcyon turns Handshake subdomains into NFTs
            with AI-generated art — permanent, beautiful, and truly yours.
          </p>

          <Button size="lg" className="btn-halcyon h-12 px-6 text-base sm:h-14 sm:px-8 sm:text-lg" onClick={handleCTA}>
            {isConnected ? "Claim Your Subdomain" : "Connect Wallet to Start"}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <p className="text-sm text-muted-foreground mt-4">
            Smart contract escrow · Auto-refund if anything fails
          </p>
        </div>
      </section>

      {/* 2. Live on Handshake — Dynamic TLD Section */}
      <section className="py-12 border-y border-border/50">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
              <Globe className="h-5 w-5 text-green-500" />
              Live on Handshake
            </h2>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              Our TLDs are owned on-chain and resolvable through decentralized DNS
            </p>
          </div>

          {tldsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="bg-card/50 border-border/50 animate-pulse">
                  <CardContent className="p-5">
                    <div className="h-6 bg-muted rounded w-3/4 mb-3" />
                    <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : tlds.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {tlds.map((tld) => (
                <Link key={tld.id} href={`/register-v2?tld=${tld.name}`}>
                  <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-colors cursor-pointer h-full">
                    <CardContent className="p-5">
                      <div className="text-lg font-bold mb-1">.{tld.name}</div>
                      <div className="text-xs text-muted-foreground mb-2">
                        {tld.domain_count} domain{tld.domain_count !== 1 ? "s" : ""} registered
                      </div>
                      <div className="text-sm text-muted-foreground mb-3">
                        From ${(tld.pricing?.tiers?.standard?.priceUSD ?? parseFloat(tld.base_price_usd)) || 10}/year
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                        <span className="text-xs text-green-600 dark:text-green-400">Live on Handshake</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
              <Badge variant="outline" className="text-base px-4 py-2">.cryptofans</Badge>
              <Badge variant="outline" className="text-base px-4 py-2">.aiworktools</Badge>
            </div>
          )}
        </div>
      </section>

      {/* 3. Problem Statement */}
      <section className="py-12 md:py-20">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">The Old Way Is Broken</h2>
            <p className="text-muted-foreground">Traditional domains weren&apos;t built for you. Halcyon is.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-card p-6 reveal">
              <CloudLightning className="h-8 w-8 text-[hsl(var(--halcyon-teal))] mb-3" />
              <h3 className="font-semibold mb-2">Unpredictable Costs</h3>
              <p className="text-sm text-muted-foreground">
                Registrars raise prices without warning. Premium domains cost
                hundreds annually just to keep.
              </p>
            </div>
            <div className="glass-card p-6 reveal" style={{ transitionDelay: "0.1s" }}>
              <Lock className="h-8 w-8 text-[hsl(var(--halcyon-teal))] mb-3" />
              <h3 className="font-semibold mb-2">You Never Truly Own It</h3>
              <p className="text-sm text-muted-foreground">
                ICANN can revoke domains. Registrars can suspend you. You never
                truly own it.
              </p>
            </div>
            <div className="glass-card p-6 reveal" style={{ transitionDelay: "0.2s" }}>
              <Ban className="h-8 w-8 text-[hsl(var(--halcyon-teal))] mb-3" />
              <h3 className="font-semibold mb-2">Trapped Value</h3>
              <p className="text-sm text-muted-foreground">
                Can&apos;t easily sell or transfer. No secondary market. A dead asset
                locked in someone else&apos;s system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. What Makes Halcyon Different — Bento Grid */}
      <section className="py-12 md:py-20 bg-secondary/10">
        <div className="container max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              The <span className="text-halcyon-gradient">Halcyon Standard</span>
            </h2>
            <p className="text-muted-foreground">
              Everything a digital identity should be. Nothing it shouldn&apos;t.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left card — spans 2 rows */}
            <div className="glass-card p-8 md:row-span-2 reveal flex flex-col justify-center" style={{ boxShadow: "0 8px 32px hsl(var(--halcyon-teal) / 0.2)" }}>
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--halcyon-teal)/0.1)] flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-[hsl(var(--halcyon-teal))]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">AI-Generated Artwork</h3>
              <p className="text-muted-foreground">
                Every subdomain is paired with unique AI art generated from your
                name. Not a generic avatar — a one-of-one piece that lives with
                your NFT forever.
              </p>
            </div>

            {/* Top right */}
            <div className="glass-card p-6 reveal" style={{ transitionDelay: "0.1s" }}>
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--halcyon-teal)/0.1)] flex items-center justify-center mb-4">
                <Hexagon className="h-6 w-6 text-[hsl(var(--halcyon-teal))]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Human-Readable Identity</h3>
              <p className="text-sm text-muted-foreground">
                Replace your 42-character wallet address with a name people remember.
              </p>
            </div>

            {/* Bottom right */}
            <div className="glass-card p-6 reveal" style={{ transitionDelay: "0.2s" }}>
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--halcyon-teal)/0.1)] flex items-center justify-center mb-4">
                <Coins className="h-6 w-6 text-[hsl(var(--halcyon-teal))]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Peaceful Renewals</h3>
              <p className="text-sm text-muted-foreground">
                Renewals at 10% of purchase price. No surprise markups. No middlemen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. How It Works */}
      <section className="py-12 md:py-20">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">How It Works</h2>
            <p className="text-muted-foreground">From connection to creation in minutes.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center reveal">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--halcyon-teal)/0.1)] flex items-center justify-center mx-auto mb-4">
                <Wallet className="h-7 w-7 text-[hsl(var(--halcyon-teal))]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Connect</h3>
              <p className="text-muted-foreground">
                Link your wallet via WalletConnect or browser extension.
              </p>
            </div>

            <div className="text-center reveal" style={{ transitionDelay: "0.1s" }}>
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--halcyon-teal)/0.1)] flex items-center justify-center mx-auto mb-4">
                <Search className="h-7 w-7 text-[hsl(var(--halcyon-teal))]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Choose</h3>
              <p className="text-muted-foreground">
                Search available subdomains across our TLDs.
              </p>
            </div>

            <div className="text-center reveal" style={{ transitionDelay: "0.2s" }}>
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--halcyon-teal)/0.1)] flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-7 w-7 text-[hsl(var(--halcyon-teal))]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Mint</h3>
              <p className="text-muted-foreground">
                Pay in FLR. Get your NFT with unique AI artwork.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button size="lg" className="btn-halcyon" onClick={handleCTA}>
              {isConnected ? "Get Started" : "Connect Wallet"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* 6. Pricing Section */}
      <section className="py-12 md:py-20 bg-secondary/10">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Pricing</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Based on subdomain length. Renewals are 10% of purchase price.
            </p>
            {pricing && (
              <p className="text-xs text-muted-foreground mt-2">
                FLR/USD: ${pricing.flrPriceUSD.toFixed(4)} ({pricing.priceSource})
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {pricingTiers.map((item) => (
              <Card
                key={item.tier}
                className={`text-center bg-card/50 border-border/50 relative ${
                  "popular" in item && item.popular
                    ? "ring-2 ring-primary"
                    : ""
                }`}
              >
                {"popular" in item && item.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                    Most Popular
                  </Badge>
                )}
                <CardHeader>
                  <Badge
                    variant="outline"
                    className="mb-2"
                    style={
                      item.color === "platinum" ? { color: "#C0C0C0", borderColor: "rgba(192,192,192,0.3)" } :
                      item.color === "gold" ? { color: "#FFD700", borderColor: "rgba(255,215,0,0.3)" } :
                      item.color === "rosegold" ? { color: "#B76E79", borderColor: "rgba(183,110,121,0.3)" } :
                      { color: "#CD7F32", borderColor: "rgba(205,127,50,0.3)" }
                    }
                  >
                    {item.chars}
                  </Badge>
                  <CardTitle className="text-lg">{item.tier}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl sm:text-3xl font-bold">
                    {item.priceFLR.toLocaleString()} FLR
                  </div>
                  <div className="text-sm text-muted-foreground">
                    ${item.priceUSD} USD
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button size="lg" onClick={handleCTA}>
              {isConnected ? "Register Now" : "Connect Wallet"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* 7. FAQ Section */}
      <section className="py-12 md:py-20">
        <div className="container max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">
            Common Questions
          </h2>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="ai-art">
              <AccordionTrigger>How does the AI artwork work?</AccordionTrigger>
              <AccordionContent>
                When you mint your subdomain, our AI generates unique artwork based on
                your chosen name using Playhouse V2.5 with custom LoRA models. The art
                becomes part of your NFT metadata.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="hex">
              <AccordionTrigger>What does &quot;use as hex address&quot; mean?</AccordionTrigger>
              <AccordionContent>
                You can associate your subdomain with your wallet address, making it
                a human-readable identifier on the Flare Network instead of a long
                hexadecimal string.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="refund">
              <AccordionTrigger>What if DNS setup fails?</AccordionTrigger>
              <AccordionContent>
                Automatic full refund via smart contract escrow. The contract only
                releases funds after successful DNS propagation verification.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="resolve">
              <AccordionTrigger>
                Will my subdomain work in normal browsers?
              </AccordionTrigger>
              <AccordionContent>
                It resolves through HNS-compatible resolvers like NextDNS and
                Fingertip. We provide setup guides for different browsers and systems.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="sell">
              <AccordionTrigger>Can I sell my subdomain?</AccordionTrigger>
              <AccordionContent>
                Yes. It&apos;s a standard ERC-721 NFT. List on any NFT marketplace,
                trade P2P, or transfer directly to another wallet.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="renewal">
              <AccordionTrigger>What are the renewal fees?</AccordionTrigger>
              <AccordionContent>
                Renewals are 10% of the original purchase price — significantly
                cheaper than traditional domain registrars.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* 7.5 TLD Owner CTA */}
      <section className="py-12 md:py-16 bg-secondary/10">
        <div className="container max-w-3xl mx-auto">
          <Card className="bg-gradient-to-r from-amber-500/5 to-amber-600/10 border-amber-500/20">
            <CardContent className="flex flex-col md:flex-row items-center gap-6 py-8 px-6 md:px-10">
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold mb-2">Own a Handshake TLD?</h3>
                <p className="text-muted-foreground">
                  Stake your TLD on Halcyon and earn 95% of every subdomain registration. Your namespace, your revenue.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link href="/stake">
                  <Button size="lg" className="btn-halcyon">
                    Stake Your TLD
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link href="/wiki/concepts/tld-staking">
                  <Button variant="outline" size="lg">
                    Learn More
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 8. Final CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[hsl(var(--halcyon-teal)/0.05)] to-[hsl(var(--halcyon-green)/0.1)]">
        <div className="container max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            <span className="text-halcyon-gradient">Your Name. Your Era.</span>
          </h2>
          <p className="text-base sm:text-xl text-muted-foreground mb-8">
            Permanent ownership. One-of-one art. An identity built to last.
          </p>

          <Button size="lg" className="btn-halcyon h-12 px-6 text-base sm:h-14 sm:px-10 sm:text-lg" onClick={handleCTA}>
            {isConnected ? "Get Started" : "Connect Wallet"}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <Shield className="h-4 w-4" /> Smart Contract Escrow
            </span>
            <span className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" /> Auto-Refund Protection
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 border-t border-border">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground">
              Halcyon — The golden era of digital identity
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link
                href="/terms"
                className="hover:text-foreground transition-colors"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </Link>
              <Link
                href="/stake"
                className="hover:text-foreground transition-colors"
              >
                Stake TLD
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
